import {
  CreateTextStyleDocument,
  DeleteTextStyleDocument,
  GetTextStylesDocument,
  GetTextStylesQuery,
} from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { addElement, updateElementProps } from "@/store/slideEditorSlice";
import type { TextElement, TextStyle } from "@ai-teach/types";
import { useMutation } from "@apollo/client/react";
import toast from "react-hot-toast";

export const useTextStyle = () => {
  const { slide, selectedId, canvasSize } = useAppSelector((state) => state.slideEditor.present);
  const dispatch = useAppDispatch();

  const selectedElement = slide?.elements.find((el) => el.id === selectedId);

  const isTextElement = selectedElement?.type === "text";

  const [createTextStyleMutation, { loading: createTextStyleLoading }] = useMutation(
    CreateTextStyleDocument,
    {
      update(cache, { data }) {
        if (!data?.createTextStyle) return;

        const id = cache.identify(data.createTextStyle);

        if (!id) return;

        cache.modify({
          fields: {
            getTextStyles(existingData) {
              return {
                ...existingData,
                items: [{ __ref: id }, ...existingData.items],
                total: (existingData.total || 0) + 1,
              };
            },
          },
        });
      },
    }
  );

  const createTextStyle = async (name: string) => {
    if (!isTextElement || !selectedElement || selectedElement.type !== "text") {
      toast.error("Please select a text element");
      return;
    }

    try {
      const input = {
        name: name,
        // Border properties
        lineStyle: selectedElement.border?.lineStyle,
        strokeWidth: selectedElement.border?.strokeWidth ?? 0,
        stroke: selectedElement.border?.stroke ?? "transparent",
        cornerRadius: selectedElement.border?.cornerRadius ?? 0,
        // Text properties from style (convert to GraphQL enum format)
        fontSize: selectedElement.style?.fontSize ?? 16,
        fontWeight: selectedElement.style?.fontWeight ?? "normal",
        color: selectedElement.style?.color ?? "#000000",
        fontFamily: selectedElement.style?.fontFamily ?? "Inter",
        textAlign: selectedElement.style?.textAlign ?? "center",
        fontStyle: selectedElement.style?.fontStyle,
        textDecoration: selectedElement.style?.textDecoration ?? "none",
      };

      await createTextStyleMutation({
        variables: {
          input,
        },
      });

      toast.success("New text style has been created !");
    } catch (_error) {
      toast.error("Failed to create a new text style !");
    }
  };

  const handleApplyTextStyle = (style: TextStyle) => {
    // Check if a text element is selected
    if (!selectedElement || selectedElement.type !== "text") {
      const canvasWidth = canvasSize.height * canvasSize.aspectRatio;
      dispatch(
        addElement({
          type: "text" as const,
          position: {
            x: Math.round((canvasWidth - 338) / 2),
            y: Math.round((canvasSize.height - 40) / 2),
          },
          content: style.name,
          size: { width: 338, height: 40 },
          style: {
            fontSize: style.fontSize ?? 16,
            fontWeight: style.fontWeight ?? undefined,
            color: style.color ?? "#000000",
            fontFamily: style.fontFamily ?? undefined,
            textAlign: style.textAlign,
            fontStyle: style.fontStyle ?? undefined,
            textDecoration: style.textDecoration ?? undefined,
          },
          border: {
            lineStyle: style.lineStyle ?? null,
            strokeWidth: style.strokeWidth ?? 0,
            stroke: style.stroke ?? "transparent",
            cornerRadius: style.cornerRadius ?? 0,
          },
        } as TextElement)
      );
      return;
    }

    // Apply the style to the selected text element
    dispatch(
      updateElementProps({
        id: selectedElement.id,
        props: {
          type: "text",
          style: {
            fontSize: style.fontSize ?? 16,
            fontWeight: style.fontWeight ?? undefined,
            color: style.color ?? "#000000",
            fontFamily: style.fontFamily ?? undefined,
            textAlign: style.textAlign,
            fontStyle: style.fontStyle ?? undefined,
            textDecoration: style.textDecoration ?? undefined,
          },
          border: {
            lineStyle: style.lineStyle ?? null,
            strokeWidth: style.strokeWidth ?? 0,
            stroke: style.stroke ?? "transparent",
            cornerRadius: style.cornerRadius ?? 0,
          },
        },
      })
    );

    toast.success("Text style applied successfully", { id: "text-style-apply" });
  };

  const [deleteTextStyleMutation, { loading: deleteTextStyleLoading }] = useMutation(
    DeleteTextStyleDocument,
    {
      update(cache, { data }) {
        if (!data?.deleteTextStyle) return;

        const id = cache.identify(data.deleteTextStyle);

        if (!id) return;

        cache.evict({ id });

        cache.gc();
      },
    }
  );

  const deleteTextStyle = (id: string) => {
    withToast(
      async () => {
        const textStyle = await deleteTextStyleMutation({ variables: { id } });
        return textStyle;
      },
      {
        loading: "Removing style...",
        error: "Failed to delete style",
        success: "Style deleted!",
      },
      "deleteTextStyleToast"
    );
  };

  return {
    createTextStyle,
    createTextStyleLoading,
    handleApplyTextStyle,
    deleteTextStyle,
    deleteTextStyleLoading,
  };
};
